// client/src/components/AndroidAppDownload.js
//
// WHERE THE ANDROID APP IS, AND HOW IT IS OFFERED — in one place.
//
// The app can come from either of two places, and the page does not care which.
// A URL in global settings — a Play listing, or a file hosted somewhere durable
// — which a system administrator can change without anybody redeploying. Or a
// copy committed under client/public, which is what this deployment uses,
// because an Expo build artifact is deleted fourteen days after it is made and
// took the download with it every time.
//
// Either way the button points at ONE address on this product's own server, so
// the link here never changes when a new build is published, and the file
// always arrives called `encatar.apk`.
//
// When there is neither, every control here renders nothing at all rather than
// offering a dead button: a download that 404s is worse than a download that
// was never advertised.
//
// ONE CLICK, ONE DOWNLOAD. An earlier version opened a panel first — what the
// app is, how big, a QR code for getting it onto a phone — and that is one tap
// too many between wanting the app and having it. The control is now a plain
// link to the file. The QR still exists, as `AndroidAppQr`, but inline on the
// landing page beside the button rather than in front of it.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Tooltip,
} from '@mui/material';
import AndroidIcon from '@mui/icons-material/Android';
import QRCode from 'qrcode';
import { useTranslation } from 'react-i18next';
import { fetchJson } from '../utils/fetchJson';

/* -------------------------------------------------------------------------- */

// ONE REQUEST PER PAGE LOAD, however many buttons ask.
//
// The top bar, the sidebar, the profile menu and a landing-page band can all be
// mounted at once; each calling the endpoint separately would be four identical
// requests for one small JSON object. The promise is cached rather than the
// answer, so simultaneous mounts share the flight rather than racing it.
let brandingFlight = null;

const loadAndroidApp = () => {
  if (!brandingFlight) {
    brandingFlight = fetchJson('/api/global-settings/branding')
      .then((data) => ({
        url: typeof data?.androidAppUrl === 'string' ? data.androidAppUrl : '',
        // A COPY SHIPPED IN THE REPOSITORY IS A DOWNLOAD WITH NO URL BEHIND IT,
        // so "is there a link set" stopped being the same question as "is there
        // anything to download". Older servers do not send this field; falling
        // back to the URL keeps the button working against one.
        available: typeof data?.androidAppAvailable === 'boolean'
          ? data.androidAppAvailable
          : Boolean(data?.androidAppUrl),
        version: typeof data?.androidAppVersion === 'string' ? data.androidAppVersion : '',
        platformName: data?.platformName || '',
        logoUrl: data?.platformLogoUrl || null,
      }))
      // A branding endpoint that is down must not take a page with it. No link
      // is a legitimate state, and it is the same state as "could not ask".
      .catch(() => ({ url: '', available: false, version: '', platformName: '', logoUrl: null }));
  }
  return brandingFlight;
};

/**
 * Where the Android app is, or an empty url when nobody has said yet.
 *
 * Exported so a caller can decide its own layout — whether to leave a gap for
 * the button, say — rather than discovering the answer only once the button has
 * already rendered nothing.
 */
export function useAndroidApp() {
  const [app, setApp] = useState({ url: '', available: false, version: '', platformName: '', logoUrl: null });

  useEffect(() => {
    let alive = true;
    loadAndroidApp().then((next) => { if (alive) setApp(next); });
    return () => { alive = false; };
  }, []);

  return app;
}

/** A store listing installs itself; a file has to be downloaded and opened. */
const isStoreLink = (url) => /play\.google\.com|galaxystore|appgallery/i.test(url || '');

// WHERE THE BUTTON ACTUALLY POINTS, which is not where the file is.
//
// EAS names its artifacts after the build —
// `application-8519d965-6a94-46c1-9104-ff8615787dd2.apk` — and a teacher told
// to find that on their phone will not. The filename is set by the response
// that delivers the bytes, so it can only be fixed by the server doing the
// delivering: this route answers with the same file under the name
// `encatar.apk`. It also means the link in this page never changes when a new
// build is published; only the settings value behind it does.
//
// A store link is sent straight on, because a listing is a page to visit rather
// than a file to name.
const DOWNLOAD_PATH = '/api/global-settings/android/encatar.apk';
const API_BASE = (
  process.env.REACT_APP_API_BASE_URL
  || process.env.REACT_APP_API_URL
  || ''
).replace(/\/+$/, '');

/**
 * A HOST THAT ALREADY NAMES THE FILE CORRECTLY IS LINKED TO DIRECTLY.
 *
 * The proxy exists to fix a filename, and it is not free: every byte of an 88MB
 * download crosses the API server, so a school putting the app on thirty phones
 * pushes 2.6GB through it. Object storage is where that traffic belongs —
 * Cloudflare R2 charges nothing for egress and serves from a CDN with proper
 * range support, so a download interrupted in a staffroom resumes instead of
 * starting over.
 *
 * An object stored under the key `encatar.apk` is already served under that
 * name, so there is nothing left for the proxy to fix and going through it
 * would be paying for a correction that has already happened.
 *
 * Everything else still goes through the server: an EAS artifact URL ends in
 * `.apk` too, but the file it hands over is called
 * `application-8519d965-6a94-46c1-9104-ff8615787dd2.apk`.
 */
const alreadyNamedRight = (url) => {
  try {
    return /\/encatar\.apk$/i.test(new URL(url).pathname);
  } catch (_) {
    return false;
  }
};

const downloadHref = (url) => {
  if (isStoreLink(url)) return url;
  if (alreadyNamedRight(url)) return url;
  return `${API_BASE}${DOWNLOAD_PATH}`;
};

/**
 * THE PROPS THAT MAKE A BUTTON A DOWNLOAD.
 *
 * `download` only takes effect same-origin — for a file served from somewhere
 * else the browser follows the response's own Content-Disposition — so it is a
 * hint, not the mechanism. `rel="noopener noreferrer"` because `target="_blank"`
 * without it hands the opened page a live `window.opener` back into this one.
 */
const linkProps = (url) => ({
  component: 'a',
  href: downloadHref(url),
  target: '_blank',
  rel: 'noopener noreferrer',
  // Same-origin only, so it is a hint rather than the mechanism — the serving
  // host's own headers are what actually name the file. Harmless where it does
  // not apply, and correct on a deployment serving both from one host.
  ...(isStoreLink(url) ? {} : { download: 'encatar.apk' }),
});

/* -------------------------------------------------------------------------- */

/**
 * The QR code for the link, inline. Shown beside a download button on a wide
 * screen, where the person reading cannot install an Android app on the device
 * they are reading it on and needs it on the phone in their pocket instead.
 */
export function AndroidAppQr({ size = 132, sx }) {
  const { t } = useTranslation();
  const { url, available } = useAndroidApp();
  const [qr, setQr] = useState('');

  // The code encodes the DOWNLOAD ADDRESS, not the settings value — a phone
  // scanning this needs somewhere to go, and with a committed APK there is no
  // settings value at all.
  const target = available ? downloadHref(url) : '';

  useEffect(() => {
    if (!target) { setQr(''); return undefined; }
    let alive = true;
    QRCode.toDataURL(target, { width: 320, margin: 1, errorCorrectionLevel: 'M' })
      .then((dataUrl) => { if (alive) setQr(dataUrl); })
      // A missing QR costs the reader a picture, not the download beside it.
      .catch(() => { if (alive) setQr(''); });
    return () => { alive = false; };
  }, [target]);

  if (!target || !qr) return null;

  return (
    <Box
      component="img"
      src={qr}
      alt={t('androidApp.qrAlt', { defaultValue: 'QR code linking to the Android app' })}
      sx={{
        width: size,
        height: size,
        borderRadius: 1.5,
        bgcolor: '#FFFFFF',
        p: 0.75,
        ...sx,
      }}
    />
  );
}

/* -------------------------------------------------------------------------- */

/**
 * A PHONE WITH THE APP ALREADY ON IT.
 *
 * The Android robot says which platform; it does not say what you would be
 * downloading, and next to a column of navigation icons it reads as one more
 * glyph. A phone outline with a real screen inside it is the only icon on the
 * page that is a picture of the thing itself.
 *
 * The screen is a cropped screenshot of the app's own overview, stored at 132px
 * wide — about three times the size it is ever drawn — so it stays sharp on a
 * retina display and still costs 7KB. The status bar is cropped off: a phone
 * frame draws its own top edge, and the clock and battery belonged to whoever
 * took the screenshot.
 *
 * The frame is `currentColor`, so it inherits the sidebar's ink and works in
 * both themes without being told which one is in force.
 */
function PhoneScreen({ height = 26 }) {
  const width = Math.round(height * 0.5);
  return (
    <Box
      aria-hidden
      sx={{
        width,
        height,
        flexShrink: 0,
        borderRadius: `${Math.max(3, Math.round(height * 0.16))}px`,
        border: '1.5px solid currentColor',
        overflow: 'hidden',
        display: 'flex',
        // Behind the image, so a slow or blocked load still looks like a phone
        // rather than like a hole in the sidebar.
        bgcolor: '#0E131A',
      }}
    >
      <Box
        component="img"
        src="/app-screen.png"
        alt=""
        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </Box>
  );
}

/**
 * The control. `variant` picks how it sits, not what it does — every one of
 * them is a link straight to the file.
 *
 *   icon      an icon button for a top bar
 *   sidebar   a row in the navigation drawer, collapsing with it
 *   menuItem  a row in an open menu
 *   nav       an outlined button for a header bar
 *   hero      a filled button for a call to action
 *   text      a plain link for a footer
 */
export function AndroidAppButton({ variant = 'nav', expanded = true, onOpen, sx, ...rest }) {
  const { t } = useTranslation();
  const { url, available } = useAndroidApp();

  const label = useMemo(
    () => t('androidApp.cta', { defaultValue: 'Get the Android app' }),
    [t],
  );

  // NOTHING AT ALL WHEN THERE IS NOTHING TO DOWNLOAD. Rendering a disabled
  // button, or one that leads nowhere, advertises a thing the school cannot
  // yet have.
  if (!available) return null;

  const link = linkProps(url);

  if (variant === 'icon') {
    return (
      <Tooltip title={label}>
        <IconButton color="inherit" onClick={onOpen} sx={sx} {...link} {...rest} aria-label={label}>
          <AndroidIcon />
        </IconButton>
      </Tooltip>
    );
  }

  if (variant === 'sidebar') {
    return (
      <Tooltip title={label} placement="right" disableHoverListener={expanded}>
        <ListItemButton
          onClick={onOpen}
          sx={{
            py: 0.8,
            px: 2,
            minHeight: 48,
            justifyContent: expanded ? 'initial' : 'center',
            ...sx,
          }}
          {...link}
          {...rest}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: expanded ? 2 : 'auto', justifyContent: 'center' }}>
            <PhoneScreen />
          </ListItemIcon>
          {/* Collapses with the drawer, the same way every other row does. */}
          <Collapse in={expanded} orientation="horizontal">
            <ListItemText
              primary={label}
              sx={{ minWidth: 0, flex: 1 }}
              primaryTypographyProps={{
                fontSize: '0.9rem',
                noWrap: true,
                sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
              }}
            />
          </Collapse>
        </ListItemButton>
      </Tooltip>
    );
  }

  if (variant === 'menuItem') {
    return (
      <MenuItem onClick={onOpen} sx={{ py: 1, ...sx }} {...link} {...rest}>
        <AndroidIcon fontSize="small" sx={{ mr: 1.25, color: '#3DDC84' }} />
        {label}
      </MenuItem>
    );
  }

  if (variant === 'text') {
    return (
      <Button
        size="small"
        variant="text"
        startIcon={<AndroidIcon />}
        onClick={onOpen}
        sx={{ justifyContent: 'flex-start', px: 0, ...sx }}
        {...link}
        {...rest}
      >
        {label}
      </Button>
    );
  }

  return (
    <Button
      variant={variant === 'hero' ? 'contained' : 'outlined'}
      size={variant === 'hero' ? 'large' : 'medium'}
      startIcon={<AndroidIcon />}
      onClick={onOpen}
      sx={sx}
      {...link}
      {...rest}
    >
      {label}
    </Button>
  );
}

export default AndroidAppButton;
